import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import onboardingActions from 'store/actions/onboarding'
import './OnboardingProgressBar.css'
import icons from 'components/Icons.js'
import { useTranslation } from 'react-i18next'

const normalizeKey = (value = '') =>
    String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

const clampPercentage = (value) => Math.max(0, Math.min(100, Number(value) || 0))

const parseHexToRgb = (hexColor) => {
    const raw = String(hexColor).replace('#', '')
    const normalized = raw.length === 3
        ? raw.split('').map((char) => char + char).join('')
        : raw

    return {
        r: parseInt(normalized.slice(0, 2), 16),
        g: parseInt(normalized.slice(2, 4), 16),
        b: parseInt(normalized.slice(4, 6), 16),
    }
}

const channelToHex = (channel) => channel.toString(16).padStart(2, '0')

const mixHexColors = (fromHex, toHex, ratio) => {
    const from = parseHexToRgb(fromHex)
    const to = parseHexToRgb(toHex)
    const safeRatio = Math.max(0, Math.min(1, ratio))

    const mixed = {
        r: Math.round(from.r + (to.r - from.r) * safeRatio),
        g: Math.round(from.g + (to.g - from.g) * safeRatio),
        b: Math.round(from.b + (to.b - from.b) * safeRatio),
    }

    return `#${channelToHex(mixed.r)}${channelToHex(mixed.g)}${channelToHex(mixed.b)}`
}

const getProgressColor = (percentage) => {
    const clamped = clampPercentage(percentage)
    const stops = [
        { at: 0, color: '#ef4444' },
        { at: 25, color: '#F57C00' },
        { at: 50, color: '#FFDD00' },
        { at: 75, color: '#7CB342' },
        { at: 100, color: '#2E7D32' },
    ]

    for (let i = 0; i < stops.length - 1; i += 1) {
        const current = stops[i]
        const next = stops[i + 1]
        if (clamped <= next.at) {
            const range = next.at - current.at || 1
            const ratio = (clamped - current.at) / range
            return mixHexColors(current.color, next.color, ratio)
        }
    }

    return '#21ba45'
}

const getIconByTitle = (title) => {
    const normalizedTitle = normalizeKey(title)
    const icon = icons.find((i) => normalizeKey(i.title) === normalizedTitle)
    return icon ? icon.url : null
}

const STEP_ROUTES = {
    api: '/wialon_connection',
    clients: '/clients/catalogs',
    employees: '/employees/employees',
    units: '/units/catalogs',
}

const STEP_ICONS = {
    api: getIconByTitle('Configuracion'),
    clients: getIconByTitle('Catalogos'),
    employees: getIconByTitle('Catalogos'),
    units: getIconByTitle('Catalogos'),
}

const OnboardingProgressBar = ({ onboarding, loadOnboardingProgress, history, user, onDismiss }) => {
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useState(false)

    useEffect(() => {
        if (!user?.can_access_tutorial) return
        loadOnboardingProgress()
    }, [loadOnboardingProgress, user?.can_access_tutorial])

    const canAccessTutorial = !!user?.can_access_tutorial
    const isTutorialOmitted = user?.tour_enabled === false

    if (!canAccessTutorial || isTutorialOmitted || onboarding.loading || onboarding.isComplete || dismissed) {
        return null
    }

    const handleStepClick = (e, step) => {
        e.stopPropagation()
        if (!step.completed) {
            const stepKey = normalizeKey(step.id)
            const route = STEP_ROUTES[stepKey]
            if (route) {
                history.push(route)
            }
        }
    }

    const getStepIcon = (step) => {
        const stepKey = normalizeKey(step.id)
        return STEP_ICONS[stepKey] || getIconByTitle('Catalogos')
    }

    const progressPercentage = clampPercentage(onboarding.percentage)
    const isIncomplete = progressPercentage < 100
    const progressColor = getProgressColor(progressPercentage)

    return (
        <div className={`onboarding-bar ${isIncomplete ? 'onboarding-bar--incomplete' : ''}`}>
            <div className="onboarding-bar__header">
                <div className="onboarding-bar__title-row">
                    <div className="onboarding-bar__steps onboarding-bar__steps--inline">
                        {onboarding.steps.map((step, index) => (
                            <React.Fragment key={step.id}>
                                <div
                                    className={`onboarding-bar__step onboarding-bar__step--inline ${step.completed ? 'onboarding-bar__step--done' : 'onboarding-bar__step--pending'}`}
                                    onClick={(e) => handleStepClick(e, step)}
                                    role={!step.completed ? 'button' : undefined}
                                    tabIndex={!step.completed ? 0 : undefined}
                                    title={step.name}
                                >
                                    <div className="onboarding-bar__step-circle">
                                        {step.completed ? (
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                <path
                                                    d="M3 8.5L6.5 12L13 4"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        ) : (
                                            <img className="onboarding-bar__step-emoji" src={getStepIcon(step)} alt={step.name} />
                                        )}
                                    </div>
                                    <span className="onboarding-bar__step-name">{step.name}</span>
                                </div>
                                {index < onboarding.steps.length - 1 && (
                                    <span className="onboarding-bar__step-connector" aria-hidden="true">
                                        &gt;
                                    </span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <span
                        className="onboarding-bar__percentage"
                        style={{ color: progressColor }}
                    >
                        {progressPercentage}%
                    </span>
                </div>
                <button
                    className="onboarding-bar__close"
                    onClick={(e) => {
                        e.stopPropagation()
                        setDismissed(true)
                        if (onDismiss) onDismiss()
                    }}
                    title={t("Ocultar")}
                >
                    x
                </button>
            </div>
        </div>
    )
}

const mapStateToProps = (state) => ({
    onboarding: state.onboarding,
    user: state.app?.user,
})

const mapDispatchToProps = (dispatch) => ({
    loadOnboardingProgress: () => dispatch(onboardingActions.loadOnboardingProgress()),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OnboardingProgressBar))

